import { Component } from 'react';
import PropTypes from 'prop-types';
import { graphql, createFragmentContainer } from 'react-relay';

// components
import { Visibility } from '../../components/global/Visibility';
import { PdpRespSimilarItem } from './PdpRespSimilarItem/PdpRespSimilarItem';
import { QuickViewProvider } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProvider } from '../../components/global/FavoriteIcons/FavoritesProvider';
import { PdpRespSimilarItemsContainer } from './PdpRespSimilarItemsContainer/PdpRespSimilarItemsContainer';

// helpers
import get from 'lodash.get';
import classnames from 'classnames';
import {
    trackSimilarItemClick,
    trackSimilarItemImpressions,
} from '../tracking/similarItemsTracking';
import { trackItemsDisplayed, trackItemClick } from '../../pdp/tracking/similarItemsTracking';

export class PdpRespSimilarItemsGridComponent extends Component {
    constructor(props) {
        super(props);

        this.onClickItem = this.onClickItem.bind(this);
        this.onContentVisible = this.onContentVisible.bind(this);
        this.hasFiredTracking = false;

        this.state = {
            contentIsVisible: false,
        };
    }

    componentDidUpdate() {
        if (
            get(this.props, 'similarItems[0].ecommerceTrackingParams') &&
            this.state.contentIsVisible &&
            !this.hasFiredTracking
        ) {
            this.hasFiredTracking = true;
            trackItemsDisplayed({ moduleType: 'PDP_SIMILAR_ITEMS', actionValue: 'bottom fold' });
            trackSimilarItemImpressions(this.getGridItems(), this.props.gridLocation);
        }
    }

    onContentVisible() {
        this.setState({ contentIsVisible: true });
    }
    onClickItem(index) {
        trackItemClick({ moduleType: 'PDP_SIMILAR_ITEMS', actionValue: 'bottom fold' });
        trackSimilarItemClick(index, this.getGridItems()[index], this.props.gridLocation);
    }
    getGridItems() {
        const gridItems = get(this.props, 'similarItems') || [];
        return this.props.processGridItems ? this.props.processGridItems(gridItems) : gridItems;
    }
    render() {
        const gridItems = this.getGridItems();

        if (!gridItems.length) {
            return null;
        }

        const { contentIsVisible } = this.state;
        const {
            isDark,
            viewer,
            currency,
            user,
            showLightBox,
            gridWrapperClass,
            gridItemWrapperClass,
            gridHeaderClass,
            favoritesLocation,
            header,
            subHeader,
            dataTn,
            scrollToRef,
            item,
            gridItemWrapperRef,
        } = this.props;
        const { isClient } = this.props.relayVariables;
        const favoritesProviderProps = {
            location: favoritesLocation,
            disable: !isClient || !contentIsVisible,
            userId: user && user.serviceId ? user.serviceId : null,
            environment: this.props.relay.environment,
            itemIds: gridItems.map(gridItem => gridItem.serviceId),
            fetchFolder: this.props.fetchFolder,
        };

        return (
            <PdpRespSimilarItemsContainer
                isDark={isDark}
                hideBar={isDark}
                header={header}
                subHeader={subHeader}
                scrollToRef={scrollToRef}
                gridWrapperClass={gridWrapperClass}
                gridHeaderClass={gridHeaderClass}
                dataTn={dataTn}
                item={item}
            >
                <Visibility onContentVisible={this.onContentVisible} />
                <FavoritesProvider {...favoritesProviderProps}>
                    {/* favorites  - passed to PdpRespFavoritingItem in PdpRespSimilarItem */}
                    {({ props: favorites }) => (
                        <QuickViewProvider>
                            {/* quickView - passed to PdpRespSimilarItemQuickView in PdpRespSimilarItem */}
                            {({ quickView }) => {
                                const itemProps = {
                                    isClient,
                                    quickView,
                                    favorites,
                                    onClick: this.onClickItem,
                                    user,
                                    showLightBox,
                                };

                                return (
                                    <div className="rowFlex">
                                        {gridItems.map((gridItem, index) => (
                                            <div
                                                key={gridItem.serviceId}
                                                className={classnames('colLg3 colMd6 colXs6', {
                                                    [gridItemWrapperClass]: !!gridItemWrapperClass,
                                                })}
                                                ref={gridItemWrapperRef}
                                            >
                                                {/* Grid of items */}
                                                <PdpRespSimilarItem
                                                    item={gridItem}
                                                    index={index}
                                                    viewer={viewer}
                                                    currency={currency}
                                                    {...itemProps}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                );
                            }}
                        </QuickViewProvider>
                    )}
                </FavoritesProvider>
            </PdpRespSimilarItemsContainer>
        );
    }
}

PdpRespSimilarItemsGridComponent.defaultProps = {
    isDark: true,
    showLightBox: true,
    dataTn: 'pdp-similar-recommendation-grid',
};

PdpRespSimilarItemsGridComponent.propTypes = {
    processGridItems: PropTypes.func,
    header: PropTypes.node.isRequired,
    subHeader: PropTypes.node,
    relayVariables: PropTypes.shape({
        userId: PropTypes.string,
        showSimilarItems: PropTypes.bool,
        isClient: PropTypes.bool.isRequired,
    }).isRequired,
    isDark: PropTypes.bool,
    showLightBox: PropTypes.bool,
    similarItems: PropTypes.array,
    relay: PropTypes.shape({
        environment: PropTypes.object.isRequired,
    }).isRequired,
    currency: PropTypes.string,
    gridItemWrapperClass: PropTypes.string,
    gridWrapperClass: PropTypes.string,
    gridHeaderClass: PropTypes.string,
    user: PropTypes.object,
    gridLocation: PropTypes.string.isRequired,
    favoritesLocation: PropTypes.string.isRequired,
    fetchFolder: PropTypes.bool,
    viewer: PropTypes.object,
    dataTn: PropTypes.string,
    scrollToRef: PropTypes.object,
    gridItemWrapperRef: PropTypes.object,
    item: PropTypes.object.isRequired,
};

export const PdpRespSimilarItemsGrid = createFragmentContainer(PdpRespSimilarItemsGridComponent, {
    viewer: graphql`
        fragment PdpRespSimilarItemsGrid_viewer on Viewer {
            ...PdpRespSimilarItem_viewer
        }
    `,
    similarItems: graphql`
        fragment PdpRespSimilarItemsGrid_similarItems on Item @relay(plural: true) {
            serviceId
            categoryPath
            browseUrl
            categoryCode
            title
            ecommerceTrackingParams
            ...PdpRespSimilarItem_item
        }
    `,
    user: graphql`
        fragment PdpRespSimilarItemsGrid_user on User {
            serviceId
            ...PdpRespSimilarItem_user
        }
    `,
    item: graphql`
        fragment PdpRespSimilarItemsGrid_item on Item {
            ...PdpRespSimilarItemsContainer_item
        }
    `,
});
